import { Badge, Banner, Card, Skeleton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Paperclip, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { type ContentFilterReviewMessage, reviewContentFilterConversationOptions } from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { AdminPage } from "../AdminPage";

function ReviewMessage({ message }: Readonly<{ message: ContentFilterReviewMessage }>) {
  const { t } = useTranslation("adminContentSafety");
  return (
    <Card>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Badge variant={message.role === "user" ? "neutral" : "info"}>
            {message.role === "user" ? t("review.roles.user") : t("review.roles.assistant")}
          </Badge>
          <Text as="span" variant="caption" tone="tertiary">
            {formatDateTime(message.createdAt)}
          </Text>
        </div>
        <Text className="wrap-anywhere whitespace-pre-wrap">{message.content}</Text>
        {message.attachmentNames.map((name) => (
          <Text key={name} as="span" variant="caption" tone="secondary" className="flex items-center gap-1">
            <Paperclip size={14} aria-hidden />
            {name}
          </Text>
        ))}
      </div>
    </Card>
  );
}

/**
 * /admin/content-safety/events/:eventUuid/conversation: the chat a flagged event happened in, read-only.
 * The only way an admin reads someone else's chat (MHS-1070); the backend audits every load, so it never retries.
 */
export function ConversationReviewPage() {
  const { t } = useTranslation("adminContentSafety");
  const { eventUuid = "" } = useParams();
  const review = useQuery({
    ...reviewContentFilterConversationOptions({ path: { event_uuid: eventUuid } }),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
  const data = review.data?.data;

  const body = () => {
    if (review.isPending) return <Skeleton className="h-32 w-full" />;
    if (review.isError || !data) {
      return (
        <Text tone="error" role="alert">
          {t("review.loadFailed")}
        </Text>
      );
    }
    return (
      <>
        <Text variant="body-sm" tone="secondary">
          {data.owner ? t("review.owner", { name: data.owner.name, email: data.owner.email }) : t("revealed.noUser")}
        </Text>
        {data.messages.length === 0 ? (
          <Text tone="secondary">{t("review.empty")}</Text>
        ) : (
          <ol className="flex flex-col gap-3" aria-label={t("review.messages")}>
            {data.messages.map((message) => (
              <li key={message.uuid}>
                <ReviewMessage message={message} />
              </li>
            ))}
          </ol>
        )}
      </>
    );
  };

  return (
    <AdminPage title={data?.title || t("revealed.untitled")} subtitle={t("review.subtitle")}>
      <Link to="/admin/content-safety" className="flex w-fit items-center gap-1 text-content-secondary">
        <ArrowLeft size={16} aria-hidden />
        <Text as="span" variant="body-sm">
          {t("review.back")}
        </Text>
      </Link>
      <Banner variant="warning" icon={<ShieldAlert size={16} />} message={t("review.notice")} />
      {body()}
    </AdminPage>
  );
}
